import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { Season } from '@/types/season';
import type { SeasonTaskStats } from '@/services/dashboard-service';
import type { SeasonHuntStats } from '@/utils/countdown-utils';
import { SEASON_TYPE_LABELS } from '@/utils/season-utils';

import { CountdownWidgetContent } from './CountdownWidget';
import './SeasonHero.css';

/** ~120ms idle debounce used to detect scroll settle where `scrollend` is unsupported. */
const SETTLE_DEBOUNCE_MS = 120;
/** Beyond this many seasons the dot row condenses and we lean on chevrons + swipe. */
const MAX_VISIBLE_DOTS = 6;

interface SeasonHeroProps {
  seasons: Season[];
  /** The committed active season id (controlled). Centers on mount; drives the active card treatment. */
  activeSeasonId: string;
  /** Called EXACTLY ONCE per settle when the settled season differs from the committed active id. */
  onActiveSeasonChange: (seasonId: string) => void;
  /** The primary season — display-only dot ring in the indicator; never forces the active page. */
  primarySeasonId: string | null;
  /** Per-season task stats, keyed by season id. */
  taskStatsBySeasonId: Record<string, SeasonTaskStats>;
  /** Sit/harvest totals for the active season only (mirrors the prior single-hero fetch). */
  huntStats?: SeasonHuntStats;
  /**
   * Optional read-only navigation callback — navigates to the prep/tasks route for the
   * given season id. Called when the in-panel task-progress link is clicked. MUST NOT
   * mutate, restyle, or re-layout any task/prep component — it only navigates.
   * When absent the progress subline is omitted.
   */
  onOpenSeasonPrep?: (seasonId: string) => void;
}

/** Human label for a season — season type + name, the same string used in the slide aria-label. */
function seasonLabel(season: Season): string {
  return `${SEASON_TYPE_LABELS[season.seasonType]} · ${season.name}`;
}

const EMPTY_STATS: SeasonTaskStats = {
  seasonId: '',
  seasonName: '',
  total: 0,
  completed: 0,
  overdue: 0,
  blocked: 0,
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function SeasonHero({
  seasons,
  activeSeasonId,
  onActiveSeasonChange,
  primarySeasonId,
  taskStatsBySeasonId,
  huntStats,
  onOpenSeasonPrep,
}: SeasonHeroProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLElement | null>>([]);

  const n = seasons.length;
  const isSingle = n === 1;

  const activeIndex = Math.max(
    0,
    seasons.findIndex((s) => s.id === activeSeasonId),
  );

  // Transient visual highlight — tracks the finger while scrolling. Distinct from the
  // committed activeSeasonId, which only changes on settle via onActiveSeasonChange.
  const [visualIndex, setVisualIndex] = useState(activeIndex);

  const rafRef = useRef<number | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didInitRef = useRef(false);

  // ── goTo: clamp + center the target page in the viewport ───────────────────
  const goTo = useCallback((rawIndex: number, behavior: ScrollBehavior) => {
    const track = trackRef.current;
    if (!track) return;
    const index = Math.min(Math.max(rawIndex, 0), pageRefs.current.length - 1);
    const page = pageRefs.current[index];
    if (!page) return;
    const left = page.offsetLeft - (track.clientWidth - page.clientWidth) / 2;
    // scrollTo is absent in non-browser environments (jsdom); fall back to scrollLeft.
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left, behavior });
    } else {
      track.scrollLeft = left;
    }
  }, []);

  // ── Find the page whose center is nearest the viewport center ──────────────
  const nearestIndex = useCallback((): number => {
    const track = trackRef.current;
    if (!track) return 0;
    const viewportCenter = track.scrollLeft + track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    pageRefs.current.forEach((page, i) => {
      if (!page) return;
      const pageCenter = page.offsetLeft + page.clientWidth / 2;
      const dist = Math.abs(pageCenter - viewportCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, []);

  // ── Controlled init: center the active page on first paint, no commit ──────
  useLayoutEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    // behavior:'auto' on first paint (and always under reduced motion).
    goTo(activeIndex, 'auto');
    setVisualIndex(activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot init; goTo/activeIndex are stable enough and re-centering on every change is handled by the controlled-sync effect below
  }, []);

  // ── Controlled sync: when activeSeasonId changes from outside, re-center ───
  // Skips the very first run (init effect already centered) via didInitRef latch.
  const lastSyncedActiveRef = useRef(activeSeasonId);
  useEffect(() => {
    if (lastSyncedActiveRef.current === activeSeasonId) return;
    lastSyncedActiveRef.current = activeSeasonId;
    // Controlled re-center always uses 'auto' (instant) — the smooth animation is
    // reserved for direct user-driven goTo (chevron/dot/keyboard). This keeps an
    // external commit from racing a smooth scroll back to a stale frame.
    goTo(activeIndex, 'auto');
    setVisualIndex(activeIndex);
  }, [activeSeasonId, activeIndex, goTo]);

  // Latch: the id committed for the in-flight settle, so a `scrollend` that fires
  // alongside the idle-debounce fallback cannot double-commit the same settle.
  // Reset whenever a fresh scroll gesture moves off the settled page.
  const settledCommitIdRef = useRef<string | null>(null);

  // ── Settle commit: fire onActiveSeasonChange ONCE when scrolling settles ───
  const commitSettle = useCallback(() => {
    const idx = nearestIndex();
    setVisualIndex(idx);
    const settledId = seasons[idx]?.id;
    if (!settledId) return;
    // Cancel any pending debounce — this settle is now resolved.
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    if (settledId === settledCommitIdRef.current) return; // already committed this settle
    settledCommitIdRef.current = settledId;
    if (settledId !== activeSeasonId) {
      onActiveSeasonChange(settledId);
    }
  }, [nearestIndex, seasons, activeSeasonId, onActiveSeasonChange]);

  // ── Scroll listener: rAF-throttled visual highlight + settle detection ─────
  // The idle-debounce is the universal settle mechanism (works in every engine,
  // including those without the `scrollend` event). Where `scrollend` IS available
  // it fires commitSettle immediately and cancels the pending debounce; the latch
  // guarantees the two paths never double-commit the same settle.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const supportsScrollEnd = 'onscrollend' in track;

    const onScroll = () => {
      // A new scroll gesture: clear the per-settle latch so the next settle commits.
      settledCommitIdRef.current = null;
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          // Transient visual update only — never commits.
          setVisualIndex(nearestIndex());
        });
      }
      // Arm the idle-debounce settle.
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(commitSettle, SETTLE_DEBOUNCE_MS);
    };

    const onScrollEnd = () => {
      commitSettle();
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    if (supportsScrollEnd) {
      track.addEventListener('scrollend', onScrollEnd);
    }
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (supportsScrollEnd) {
        track.removeEventListener('scrollend', onScrollEnd);
      }
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [nearestIndex, commitSettle]);

  // ── Mouse drag-to-scroll (mouse pointer ONLY — never fights native touch) ──
  const dragRef = useRef<{ startX: number; startScroll: number; active: boolean }>({
    startX: 0,
    startScroll: 0,
    active: false,
  });

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return; // ignore touch/pen — native scroll-snap handles those
    const track = trackRef.current;
    if (!track) return;
    dragRef.current = { startX: e.clientX, startScroll: track.scrollLeft, active: true };
    track.style.scrollSnapType = 'none';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const track = trackRef.current;
    if (!track) return;
    track.scrollLeft = dragRef.current.startScroll - (e.clientX - dragRef.current.startX);
  }, []);

  const endDrag = useCallback(() => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const track = trackRef.current;
    if (track) track.style.scrollSnapType = 'x mandatory';
    goTo(nearestIndex(), prefersReducedMotion() ? 'auto' : 'smooth');
  }, [goTo, nearestIndex]);

  // ── Keyboard nav on the focused track ──────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goTo(visualIndex - 1, behavior);
          break;
        case 'ArrowRight':
          e.preventDefault();
          goTo(visualIndex + 1, behavior);
          break;
        case 'Home':
          e.preventDefault();
          goTo(0, behavior);
          break;
        case 'End':
          e.preventDefault();
          goTo(n - 1, behavior);
          break;
        default:
          break;
      }
    },
    [goTo, visualIndex, n],
  );

  if (n === 0) return null;

  const userBehavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Season countdown"
      data-testid="season-hero"
      data-active-season-id={activeSeasonId}
      className="max-w-2xl w-full"
      style={{
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      {/*
        Single surface — ONE card, NO nested inner card from CountdownWidgetContent.
        Token mapping (dark mode):
          bg  → var(--color-surface)  = #252320  ✓ matches siblings exactly
          border → var(--color-border) = #3d3a35  (sibling hex #2d2a26 = --color-surface-elevated: DRIFT)
          shadow → var(--shadow-card)  = 0 1px 3px … (sibling uses heavy custom shadow: DRIFT)
        Drift in PrepCenter/ConditionsWidget (border #2d2a26 ≠ --color-border, heavy shadow ≠
        --shadow-card) filed as separate cleanup; SeasonHero uses correct tokens here.
        Layout guard: block display (not flex-col) so flex:0 0 100% page basis is
        never interpreted as height. overflow-hidden clips the scroll track within the radius.
      */}
      <div
        data-testid="season-hero-card"
        className="rounded-lg overflow-hidden"
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '0.5px solid var(--color-border)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* Track viewport + edge chevrons share ONE positioning context (relative) so the
            prev/next chevrons can vertically center on the track (~ the numeral) while
            overlaying only the LEFT/RIGHT card edges. .deck-track scroll mechanics unchanged. */}
        <div className="relative">
          {/* Scroll track — NOT the card; must not become a column-flex container. */}
          <div
            ref={trackRef}
            className="deck-track"
            data-testid="deck-track"
            role="group"
            tabIndex={0}
            aria-label="Season slides"
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            onPointerCancel={endDrag}
          >
            {seasons.map((season, i) => {
              const stats = taskStatsBySeasonId[season.id] ?? { ...EMPTY_STATS, seasonId: season.id };

              // Prep-link subline text — same switch as CountdownWidget's TaskProgress.
              // Shown ONLY via the §5 link so there is EXACTLY ONE prep prompt per panel.
              const prepLinkText =
                stats.total > 0
                  ? `${stats.completed}/${stats.total} tasks complete`
                  : 'Add tasks to track your prep';

              return (
                <article
                  key={season.id}
                  ref={(el) => {
                    pageRefs.current[i] = el;
                  }}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`Season ${i + 1} of ${n}: ${seasonLabel(season)}`}
                  {...(season.id === activeSeasonId ? { 'aria-current': 'true' } : {})}
                  className={isSingle ? 'deck-page deck-page--solo' : 'deck-page'}
                  data-layout={isSingle ? 'solo' : 'deck'}
                >
                  {/*
                    CountdownWidgetContent renders the content only — no card shell (no
                    bg/border/shadow/radius). The outer season-hero-card IS the single surface.
                    No TaskProgress subline — the §5 prep-link button is the only prep prompt.
                  */}
                  <CountdownWidgetContent
                    season={season}
                    huntStats={season.id === activeSeasonId ? huntStats : undefined}
                  />

                  {/* In-panel prep link (§5) — the ONLY prep prompt per panel.
                      Read-only: calls onOpenSeasonPrep (navigate only). When absent: omitted.
                      minHeight:'44px' inline is the regression-lock source of truth for ≥44px
                      touch target (assertable in jsdom, not just a Tailwind class). */}
                  {onOpenSeasonPrep != null ? (
                    <button
                      type="button"
                      data-testid="season-hero-prep-link"
                      data-season-id={season.id}
                      onClick={() => onOpenSeasonPrep(season.id)}
                      className="w-full inline-flex items-center justify-center text-[10px]"
                      style={{
                        color: 'var(--color-text-muted)',
                        minHeight: '44px',
                      }}
                    >
                      {prepLinkText}
                    </button>
                  ) : null}
                </article>
              );
            })}
          </div>

          {/* Edge chevrons (relocated from the indicator row, fix-pass) — absolutely
              positioned at the LEFT/RIGHT edges of the card, vertically centered on the
              track (~ the numeral). Rendered ONLY for multi-season (same suppression as the
              dots). 44px hit target via inline minWidth/minHeight (jsdom-readable per
              RULE-010, mirroring the prep-link). left-1/right-1 insets clear a 3-digit
              numeral. Each chevron is the lone pointer-events target in its own box, so it
              never blankets the scroll track nor swallows swipe/drag gestures that begin away
              from the edges. Disabled/dimmed at the ends. */}
          {!isSingle && (
            <>
              <button
                type="button"
                aria-label="Previous season"
                data-testid="deck-chevron-prev"
                disabled={visualIndex === 0}
                onClick={() => goTo(visualIndex - 1, userBehavior)}
                className="absolute left-1 top-1/2 z-10 -translate-y-1/2 inline-flex items-center justify-center disabled:opacity-30"
                style={{ minWidth: '44px', minHeight: '44px', color: 'var(--color-text-secondary)' }}
              >
                <ChevronLeft size={18} aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Next season"
                data-testid="deck-chevron-next"
                disabled={visualIndex === n - 1}
                onClick={() => goTo(visualIndex + 1, userBehavior)}
                className="absolute right-1 top-1/2 z-10 -translate-y-1/2 inline-flex items-center justify-center disabled:opacity-30"
                style={{ minWidth: '44px', minHeight: '44px', color: 'var(--color-text-secondary)' }}
              >
                <ChevronRight size={18} aria-hidden />
              </button>
            </>
          )}
        </div>

        {/*
          Dots-only indicator (chevrons relocated to the card edges, fix-pass). Still INSIDE
          the card and below the track — static chrome that does not scroll with the panels.
          Compact: the visual dot row stays ~32px (trimmed bottom padding pb-0.5) and pulls
          the dots toward the prep link without shrinking the numeral or the 44px prep target.
          Each dot still meets the §9 ≥44px interactive target — the 44px touch box is pulled
          back out of layout with equal negative vertical margins (margin box = 32px), so the
          AC holds while the row height and panel equal-heights are unchanged (no swipe jump).
          Single-season: indicator still hidden.
        */}
        {!isSingle && (
          <div
            className="deck-indicator flex flex-row items-center justify-center pb-0.5"
            data-testid="deck-indicator"
          >
            <div role="tablist" aria-label="Select season" className="flex flex-row items-center gap-2">
              {seasons.slice(0, MAX_VISIBLE_DOTS).map((season, i) => {
                const isVisualActive = i === visualIndex;
                const isPrimary = season.id === primarySeasonId;
                // Primary-but-not-active dot still carries the ember ring locator (change 2 keeps this).
                const showRing = isPrimary && !isVisualActive;
                return (
                  <button
                    key={season.id}
                    type="button"
                    role="tab"
                    aria-label={`Season ${i + 1} of ${n}: ${seasonLabel(season)}`}
                    {...(isVisualActive ? { 'aria-current': 'true' } : {})}
                    onClick={() => goTo(i, userBehavior)}
                    className="inline-flex items-center justify-center min-w-[44px]"
                    style={{
                      // §9 AC: ≥44px interactive target WITHOUT undoing the visual compaction.
                      // The touch target (border box) is the full 44px tall, but equal negative
                      // vertical margins pull it back OUT of flex layout so the row measures only
                      // the 32px margin box — the rendered dot and the indicator row stay compact
                      // (no height jump). Horizontal target is already 44px (min-w-[44px]) with
                      // gap-2 spacing, so adjacent dot targets do not overlap. Inline (not a
                      // Tailwind class) so the contract is jsdom-readable per RULE-010.
                      minHeight: '44px',
                      marginTop: '-6px',
                      marginBottom: '-6px',
                    }}
                  >
                    <span
                      aria-hidden
                      className="deck-dot inline-block"
                      style={{
                        width: isVisualActive ? 22 : 7,
                        height: 7,
                        borderRadius: '9999px',
                        backgroundColor: isVisualActive
                          ? 'var(--color-accent)'
                          : 'var(--color-bark-500)',
                        boxShadow: showRing
                          ? '0 0 0 1.5px var(--color-ember-tint-border)'
                          : undefined,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
